
import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';
import { AppCommonModule } from 'src/app/common.module';
import { Contractor } from 'src/app/models/contractor.model';
import { ContractorsService } from 'src/app/services/contractors.service';
import { SitesService } from 'src/app/services/sites.service';
import { UsersService } from 'src/app/services/users.service';
import { Roles } from 'src/app/utility/role';
import { hasApplicationAccess } from 'src/app/utility/site-activation';

type FirstLoginStepKey = 'welcome' | 'howItWorks' | 'fieldOs' | 'workspace' | 'terms' | 'ready';
type WorkspaceChoice = 'create' | 'join' | 'later';

@Component({
  selector: 'app-first-login-dialog',
  templateUrl: './first-login.dialog.html',
  styleUrls: ['./first-login.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
  ],
})
export class FirstLoginDialogComponent {

  private _modal = inject(ModalController);
  private contractorsService = inject(ContractorsService);
  private sitesService = inject(SitesService);
  private usersService = inject(UsersService);

  private readonly stepOrderWithWorkspace: FirstLoginStepKey[] = ['welcome', 'howItWorks', 'fieldOs', 'workspace', 'terms', 'ready'];
  private readonly stepOrderWithoutWorkspace: FirstLoginStepKey[] = ['welcome', 'howItWorks', 'fieldOs', 'terms', 'ready'];
  readonly step = signal(1);
  readonly termsAccepted = signal(false);
  readonly workspaceChoice = signal<WorkspaceChoice>(null);
  readonly workspaceName = signal('');
  readonly workspaceLoading = signal(false);
  readonly workspaceCreated = signal(false);
  readonly workspaceError = signal<string>(null);
  readonly ordererProvisioning = signal(false);
  readonly hasLinkedOrganization = computed(() => {
    const user = this.usersService.currentUserS();
    const hasContractors = (user?.contractors ?? []).some((id) => typeof id === 'string' && id.trim().length > 0);
    return hasContractors;
  });

  readonly requiresWorkspaceCreation = computed(() => [Roles.admin].includes(this.usersService.role()) && !this.hasLinkedOrganization());
  readonly stepOrder = computed<FirstLoginStepKey[]>(() => this.requiresWorkspaceCreation() ? this.stepOrderWithWorkspace : this.stepOrderWithoutWorkspace);
  readonly userName = computed(() => this.usersService.currentUserS()?.displayName?.trim() ?? '');
  readonly totalSteps = computed(() => this.stepOrder().length);
  readonly currentStep = computed(() => Math.min(this.step(), this.totalSteps()));
  readonly currentStepKey = computed(() => this.stepOrder()[this.step() - 1] ?? this.stepOrder()[this.stepOrder().length - 1]);
  readonly progressFillClass = computed(() => `step-${this.currentStep()}-of-${this.totalSteps()}`);
  readonly isTermsStep = computed(() => this.currentStepKey() === 'terms');
  readonly isWorkspaceStep = computed(() => this.currentStepKey() === 'workspace');
  readonly isReadyStep = computed(() => this.currentStepKey() === 'ready');
  readonly isWelcomeStep = computed(() => this.currentStepKey() === 'welcome');
  readonly showSkipIntro = computed(() => this.step() < 4 && !this.isWelcomeStep());
  readonly canContinue = computed(() => {
    if (this.isTermsStep()) {
      return this.termsAccepted();
    }
    if (this.isWorkspaceStep()) {
      const choice = this.workspaceChoice();
      if (choice == null) {
        return false;
      }
      return choice === 'create' ? this.workspaceCreated() : true;
    }
    return true;
  });

  constructor() {
    effect(() => {
      const userGuid = this.usersService.currentUserS()?.guid;
      if (userGuid) {
        void this.ensureOrdererClientAndSite();
      }
    });
  }

  private async ensureOrdererClientAndSite() {
    const user = this.usersService.currentUserS();
    if (!user || !hasApplicationAccess(user) || user.role === Roles.manager || this.ordererProvisioning()) {
      return;
    }

    const existingWorksites = (user.worksites ?? []).filter((guid) => typeof guid === 'string' && guid.trim().length > 0);
    if (existingWorksites.length > 0) {
      return;
    }

    if ((user.company ?? '').trim().length < 2) {
      return;
    }

    this.ordererProvisioning.set(true);
    try {
      const result = await firstValueFrom(this.sitesService.provisionFirstLogin());
      user.worksites = [result.site];
    } catch (error) {
      console.error(error);
    } finally {
      this.ordererProvisioning.set(false);
    }
  }

  prevStep() {
    if (this.step() > 1) {
      this.step.update((value) => value - 1);
    }
  }

  nextStep() {
    if (!this.canContinue()) {
      return;
    }

    if (this.step() < this.totalSteps()) {
      this.step.update((value) => value + 1);
      return;
    }

    this.finish();
  }

  skipIntro() {
    this.step.set(4);
  }

  onWorkspaceName(value: string | null) {
    const normalized = value == null ? '' : value.trimStart();
    this.workspaceName.set(normalized);
  }

  onWorkspaceChoice(choice: WorkspaceChoice) {
    this.workspaceChoice.set(choice);
    this.workspaceError.set(null);
  }

  async createWorkspace() {
    const currentUser = this.usersService.currentUserS();
    const name = this.workspaceName().trim();
    if (currentUser == null || name.length < 2 || this.workspaceLoading()) {
      return;
    }

    this.workspaceLoading.set(true);
    this.workspaceError.set(null);
    try {
      const company: Contractor = {
        name,
        email: currentUser.email ?? '',
        license: 'free',
        created: new Date(),
        licenseStart: new Date(),
        disabled: false,
        sites: [],
        clients: [],
      };
      const doc = await this.contractorsService.save(company);
      await this.usersService.updateOnly(currentUser.guid, {
        contractors: [doc.id],
        onboardingStatus: 'workspace-created',
      });
      this.workspaceCreated.set(true);
    } catch (error) {
      console.error(error);
      this.workspaceError.set('firstLogin.workspace.createError');
    } finally {
      this.workspaceLoading.set(false);
    }
  }

  onTermsAccepted(event: Event) {
    const checked = (event as CustomEvent<{ checked: boolean }>).detail.checked;
    this.termsAccepted.set(checked);
  }

  finish() {
    this._modal.dismiss({ acceptedTerms: true });
  }

}
